/**
 * Reading what the CS2 console prints for `getpos`:
 *   setpos 1136.000000 32.000000 -165.968750;setang 3.234000 -179.974365 0.000000
 * `getpos_exact` prints setpos_exact / setang_exact; both are accepted.
 */
export interface Position {
  x: number;
  y: number;
  z: number;
}

export interface PlayerView extends Position {
  /** Up/down angle: negative looks up. */
  pitch: number;
  /** Left/right angle. */
  yaw: number;
}

const NUM = String.raw`(-?\d+(?:\.\d+)?(?:e-?\d+)?)`;
const SETPOS = new RegExp(String.raw`setpos(?:_exact)?\s+${NUM}\s+${NUM}\s+${NUM}`, "i");
const SETANG = new RegExp(String.raw`setang(?:_exact)?\s+${NUM}\s+${NUM}(?:\s+${NUM})?`, "i");

/** Position and view angles from a getpos line, or null when the text has no setpos. */
export function parseGetpos(text: string): (Position & Partial<Pick<PlayerView, "pitch" | "yaw">>) | null {
  const pos = SETPOS.exec(text);
  if (!pos) return null;
  const result: Position & Partial<Pick<PlayerView, "pitch" | "yaw">> = {
    x: Number(pos[1]),
    y: Number(pos[2]),
    z: Number(pos[3]),
  };
  const ang = SETANG.exec(text.slice(pos.index + pos[0].length));
  if (ang) {
    result.pitch = Number(ang[1]);
    result.yaw = Number(ang[2]);
  }
  if (![result.x, result.y, result.z].every(Number.isFinite)) return null;
  return result;
}

const fmt = (n: number) => Number(n.toFixed(6)).toString();

/** The console line that puts a player back on this exact spot and view. */
export function teleportCommand(v: PlayerView): string {
  return `setpos ${fmt(v.x)} ${fmt(v.y)} ${fmt(v.z)};setang ${fmt(v.pitch)} ${fmt(v.yaw)} 0`;
}
